/**
 * Starting and stopping a session, from the phone.
 *
 * The same shape as canvas.js and for the same reasons: the page sends the
 * Cognito ID token it already holds, the API does the rest, and nothing here
 * touches the DOM so the rules are testable without a browser.
 *
 * One failure is named separately. "A session is already running" is not an
 * error the student caused and not one a retry fixes -- it means the other
 * device got there first, and the right answer on screen is to show the
 * session that exists rather than a red line about the one that did not
 * start. The screen can only do that if this tells the two apart.
 *
 * Everything else collapses to `SessionUnavailable`, deliberately: a page
 * that distinguishes six kinds of not-working gives the student six ways to
 * read the same single fact.
 */

export class SessionConflict extends Error {
  constructor(message) {
    super(message || 'A session is already running.');
    this.name = 'SessionConflict';
    // What the screen keys off, rather than matching on the message.
    this.alreadyRunning = true;
  }
}

export class SessionUnavailable extends Error {
  constructor(message) {
    super(message || 'Could not reach the timer.');
    this.name = 'SessionUnavailable';
    this.alreadyRunning = false;
  }
}

async function call(config, session, path, { method = 'GET', body } = {}, fetchImpl = fetch) {
  if (!config?.sessionsApiUrl) {
    // Checked before the network so a site deployed without the API says so
    // instead of spending a round trip to find out.
    throw new SessionUnavailable('Timers are not set up for this site yet.');
  }

  const init = {
    method,
    headers: { authorization: `Bearer ${session?.idToken}` },
  };
  if (body !== undefined) {
    init.headers['content-type'] = 'application/json';
    init.body = JSON.stringify(body);
  }

  let response;
  try {
    response = await fetchImpl(`${config.sessionsApiUrl}${path}`, init);
  } catch {
    throw new SessionUnavailable('No connection.');
  }

  if (response.status === 401 || response.status === 403) {
    throw new SessionUnavailable('Your sign-in has expired. Sign in again.');
  }

  let payload = null;
  try {
    payload = await response.json();
  } catch {
    payload = null;
  }

  if (response.status === 409) throw new SessionConflict(payload?.message);
  if (!response.ok) throw new SessionUnavailable(payload?.message);
  return payload;
}

/**
 * Begin a session.
 *
 * `workItemId` attaches it to an assignment, which is what makes the time
 * count as work on that thing rather than as an unattached stretch. Absent
 * for an unscheduled break, which is the whole difference between the two.
 */
export async function startSession(config, session, reason, workItemId = null, fetchImpl) {
  const body = await call(config, session, '/sessions',
    { method: 'POST', body: { reason, workItemId } }, fetchImpl);
  return body?.session ?? null;
}

/** Encoded, not interpolated: an id is data, and a URL is not a template. */
const at = (id) => `/sessions/${encodeURIComponent(id)}`;

async function patch(config, session, id, body, fetchImpl) {
  const answer = await call(config, session, at(id), { method: 'PATCH', body }, fetchImpl);
  return answer?.session ?? null;
}

/**
 * Hold a session, and say which way.
 *
 * `kind` is 'break' or 'pause'. Both stop the work clock; only a break
 * accrues to the not-counted one. Sent rather than assumed, because the
 * desktop sends it too and a hold whose kind was decided locally is a hold
 * the two devices disagree about -- which is what Allen meant by "make sure
 * that the values are synced because they currently are not".
 */
export function pauseSession(config, session, id, kind = 'break', fetchImpl) {
  return patch(config, session, id, { action: 'pause', kind }, fetchImpl);
}

export function resumeSession(config, session, id, fetchImpl) {
  return patch(config, session, id, { action: 'resume' }, fetchImpl);
}

/**
 * End it now, or at the moment the student says it really ended.
 *
 * `endedAt` is how "this was still running from 3pm yesterday -- when did it
 * end?" is answered: it replaces a capped guess with a fact.
 */
export function endSession(config, session, id, endedAt, fetchImpl) {
  const body = endedAt == null ? { action: 'end' } : { action: 'end', endedAt };
  return patch(config, session, id, body, fetchImpl);
}

export async function currentSession(config, session, fetchImpl) {
  const body = await call(config, session, '/sessions/current', {}, fetchImpl);
  return body?.session ?? null;
}

export async function listSessions(config, session, since = 0, fetchImpl) {
  const body = await call(config, session,
    `/sessions?since=${encodeURIComponent(since)}`, {}, fetchImpl);
  return body?.sessions ?? [];
}

/* ── done-flags ─────────────────────────────────────────────── */

/**
 * Alter's own record that something on today's list is finished.
 *
 * Never a claim about Canvas: nothing in this system can write to Canvas, so
 * the word on screen is "done" and `source` says which of three different
 * facts this actually was.
 *
 * The key is encoded rather than interpolated for the same reason a session
 * id is: a calendar event id is Google's string, not ours, and a key that
 * reached the path raw could name a route the caller did not mean.
 */
const doneAt = (key) => `/todo/${encodeURIComponent(key)}/done`;

export async function markDone(config, session, key, source = 'checked', fetchImpl) {
  const body = await call(config, session, doneAt(key),
    { method: 'PUT', body: { source } }, fetchImpl);
  return body?.done ?? null;
}

export async function clearDone(config, session, key, fetchImpl) {
  await call(config, session, doneAt(key), { method: 'DELETE' }, fetchImpl);
}

/** Every flag set at or after `since`, as `{itemKey, doneAt, source}`. */
export async function listDone(config, session, since = 0, fetchImpl) {
  const body = await call(config, session,
    `/todo/done?since=${encodeURIComponent(since)}`, {}, fetchImpl);
  return body?.done ?? [];
}

/* ── practice test attempts ───────────────────────────────────
 *
 * Allen, 2026-08-22: "at the end when I get something wrong this should get
 * remembered by alter, these should be sent back so that alter can know this
 * and use this in the future to create tests that challenge me."
 *
 * Through this API rather than S3 because the browser is deliberately
 * READ-only under `study/` — the trust runs the other way there — and
 * because what goes back is structured data with a schema, which is what
 * this API is for.
 *
 * The ANSWERS go, not the score. Alter re-marks: it is the only side that
 * can read a written answer, and a score it could not check would be a
 * number it had to take on trust.
 */
export async function submitAttempt(config, session, attempt, fetchImpl) {
  const body = await call(config, session, '/tests/attempts',
    { method: 'POST', body: attempt }, fetchImpl);
  return body?.attempt ?? null;
}

/** Every attempt recorded at or after `since`, for the desktop to collect. */
export async function listAttempts(config, session, since = 0, fetchImpl) {
  const body = await call(config, session,
    `/tests/attempts?since=${encodeURIComponent(since)}`, {}, fetchImpl);
  return body?.attempts ?? [];
}
